<template>
    <div
        v-if="ui.selectionTool.enabled && ui.selectionTool.opened && !addOnBeingEdited"
        ref="focusTrapContainer"
        role="dialog"
        aria-modal="true"
        :aria-label="localizations[lang].tooltips.selectionTools"
        class="ReaderSelectionTool transition-width fixed left-1/2 top-[calc(100%-375px)] z-99 -mt-4 -translate-x-1/2 transform select-none text-theme-text shadow-lg duration-100 ease-in-out"
        :style="ui.selectionTool.position"
    >
        <div
            :class="[
                'p-0',
                'rounded',
                'border',
                'px-0',
                'py-2',
                'm-0',
                'flex',
                'flex-col',
                'bg-theme-bg',
                'justify-around',
                'border-theme-border',
                'min-h-12',
                '-mr-5',
            ]"
        >
            <div class="flex items-center px-2">
                <transition name="fadeTools" mode="out-in">
                    <div v-show="ui.selectionTool.opened" class="flex items-center">
                        <ColorButton
                            v-if="!isSharedHighlight"
                            :apply-color-and-confirm-selection="applyColorAndConfirmSelection"
                            :toggle-colors-menu="toggleColorsMenu"
                            :show-colors-items="showColorsItems"
                            :submenu-display-in-down="submenuDisplayInDown"
                            @delete-selection="deleteSelection"
                        />
                        <BaseButton
                            v-if="canUseNotes && !isSharedHighlight"
                            class="text-theme-toolbar-icon"
                            dusk="create-note"
                            icon="note_add-24px"
                            data-action="create-note"
                            :tooltip="localizations[lang].tooltips.createNote"
                            @click="createNote"
                        />
                        <BaseButton
                            v-if="canUseFlashcards && !isSharedHighlight"
                            class="text-theme-toolbar-icon"
                            dusk="create-flashcard"
                            icon="flashcard-24px"
                            data-action="create-flashcard"
                            :tooltip="localizations[lang].flashcard.createFromSelection"
                            @click="createFlashcardFromSelection"
                        />
                        <AddOnButton
                            v-if="canEditAddOns && !isSharedHighlight"
                            @create-add-on="createAddOnFromSelection"
                        />
                        <span
                            v-if="!isSharedHighlight"
                            class="relative mx-2 h-6 w-px after:absolute after:h-6 after:w-px after:bg-theme-border after:content-['']"
                        ></span>
                        <IAButton
                            v-if="isAiEnabled"
                            class="m-0 mx-1"
                            :show-ai-integration="showAiIntegration"
                            :toggle-a-i-menu="toggleAIMenu"
                            :menu-ai-is-showing="menuAiIsShowing"
                            :submenu-display-in-down="submenuDisplayInDown"
                            :is-desktop="isDesktop"
                            :open-toast="openToast"
                        />
                        <SelectionCitationMenu
                            v-if="copyContentToClipboard.enabled"
                            class="m-0 mx-1"
                            :menu-copy-is-showing="menuCopyIsShowing"
                            :submenu-display-in-down="submenuDisplayInDown"
                            @toggle-copy-menu="toggleCopyMenu"
                        />
                        <DictionaryButton
                            class="m-0 mx-1 hidden md:block"
                            :text="currentHighlight.totalText"
                            :is-desktop="isDesktop"
                            :open-toast="openToast"
                            @dictionary="handleDictionary"
                        />
                        <TranslateButton
                            v-if="showActionTranslateInMainModal"
                            class="m-0 mx-1 hidden md:block"
                            :text="currentHighlight.totalText"
                            :is-desktop="isDesktop"
                            :open-toast="openToast"
                            @translate="handleTranslate"
                        />
                        <ListenButton
                            v-if="ui.listening.enabled"
                            class="m-0 mx-1 hidden md:block"
                            :text="currentHighlight.totalText"
                            :is-desktop="isDesktop"
                            :open-toast="openToast"
                            @listen="handleListen"
                        />
                        <SocialShareButton
                            v-if="showActionSocialShare"
                            :show-we-chat-share="showWeChatShare"
                            class="m-0 mx-1 hidden md:block"
                            :menu-share-is-showing="menuShareIsShowing"
                            :submenu-display-in-down="submenuDisplayInDown"
                            @update:show-we-chat-share="showWeChatShare = $event"
                            @toggle-share-menu="toggleShareMenu"
                        />
                        <div class="relative m-0 mx-1 md:hidden">
                            <BaseButton
                                :aria-label="localizations[lang].tooltips.moreOptions"
                                icon="ellipsis-vert"
                                class="text-theme-toolbar-icon"
                                @click="toggleMoreOptions"
                            />
                            <transition name="slide-fade">
                                <ListDropdown
                                    v-show="menuMoreOptions"
                                    class="-right-[9px]"
                                    :submenu-display-in-down="submenuDisplayInDown"
                                >
                                    <ListItem>
                                        <DictionaryButton
                                            with-text
                                            :text="currentHighlight.totalText"
                                            :is-desktop="isDesktop"
                                            :open-toast="openToast"
                                            @dictionary="handleDictionary"
                                        />
                                    </ListItem>
                                    <ListItem v-if="showActionTranslateInMainModal">
                                        <TranslateButton
                                            with-text
                                            :text="currentHighlight.totalText"
                                            :is-desktop="isDesktop"
                                            :open-toast="openToast"
                                            @translate="handleTranslate"
                                        />
                                    </ListItem>
                                    <ListItem v-if="ui.listening.enabled">
                                        <ListenButton
                                            with-text
                                            :text="currentHighlight.totalText"
                                            :is-desktop="isDesktop"
                                            :open-toast="openToast"
                                            @listen="handleListen"
                                        />
                                    </ListItem>
                                    <ListItem v-if="showActionSocialShare">
                                        <SocialShareButton
                                            :show-we-chat-share="showWeChatShare"
                                            :menu-share-is-showing="menuShareIsShowing"
                                            :btn-text="localizations[lang].tooltips.share"
                                            :submenu-display-in-down="submenuDisplayInDown"
                                            @update:show-we-chat-share="showWeChatShare = $event"
                                            @toggle-share-menu="toggleShareMenu"
                                        />
                                    </ListItem>
                                </ListDropdown>
                            </transition>
                        </div>
                    </div>
                </transition>
            </div>
        </div>
    </div>
</template>

<script>
'use strict';

import AddOnButton from './actions/AddOnButton.vue';
import ColorButton from './actions/ColorButton.vue';
import IAButton from './actions/IAButton.vue';
import SocialShareButton from './actions/SocialShareButton.vue';
import SelectionCitationMenu from './actions/SelectionCitationMenu.vue';
import DictionaryButton from './actions/DictionaryButton.vue';
import TranslateButton from './actions/TranslateButton.vue';
import BaseButton from '../Components/buttons/BaseButton.vue';
import { pendingHighlightDeletes } from '_shared_/js/utils/pendingHighlightDeletes.js';
import ListDropdown from '../Components/list/ListDropdown.vue';
import ListItem from '../Components/list/ListItem.vue';
import focusTrapMixin from '../../js/mixins/focusTrapMixin';
import { textAnchorFromHighlight } from '../AddOns/addOnAnchorFromSelection';

import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { uiAPI } from '../../../src/js/communication';

export default {
    components: {
        AddOnButton,
        DictionaryButton,
        TranslateButton,
        ListenButton: () => import('./actions/ListenButton.vue'),
        BaseButton,
        SelectionCitationMenu,
        ColorButton,
        IAButton,
        SocialShareButton,
        ListItem,
        ListDropdown,
    },
    mixins: [focusTrapMixin],
    data() {
        return {
            minLengthSelectedTextForAI: 10,
            maxLengthSelectedTextForAI: 4000,

            menuIsShowing: false,
            menuCopyIsShowing: false,
            menuAiIsShowing: false,
            menuShareIsShowing: false,
            menuMoreOptions: false,
            plainCopied: false,
            showColorsItems: false,
            showWeChatShare: false,

            currentBreakpoint: '',
            focusTrapEnabled: false,
            focusTrapAutoFocus: false,
        };
    },
    computed: {
        ...mapState([
            'device',
            'integrations',
            'localizations',
            'lang',
            'anonymousSession',
            'currentHighlight',
            'ui',
            'issue',
            'title',
            'lastLocation',
            'isOffline',
            'AiIntegrations',
            'copyContentToClipboard',
            'aiActions',
            'userIsGuest',
            'sharedColors',
        ]),
        ...mapGetters([
            'currentPage',
            'isEpub',
            'canUseNotes',
            'canUseFlashcards',
            'onlyMobileVisible',
            'canUseNativeShare',
            'isShareEnabled',
            'canEditAddOns',
            'addOnBeingEdited',
        ]),

        isDesktop() {
            return !this.onlyMobileVisible;
        },
        showActionSocialShare() {
            return this.copyContentToClipboard.enabled;
        },
        showActionTranslateInMainModal() {
            return this.isDesktop || this.showActionSocialShare;
        },
        issueHighlightUrlForShare() {
            const location = this.isEpub ? btoa(JSON.stringify(this.lastLocation.epub)) : this.currentPage;
            return `${this.issue.issue_url}?location=${location}`;
        },

        isAiEnabled() {
            return (
                this.AiIntegrations.enabled &&
                !this.userIsGuest &&
                this.aiActions &&
                Object.entries(this.aiActions).length > 0
            );
        },

        /**
         * Whether the current highlight uses a color shared by another author.
         * Shared highlights are read-only for the subscribed user: color change,
         * deletion and note creation must be hidden from the selection tool.
         * @returns {boolean}
         */
        isSharedHighlight() {
            const colorSlug = this.currentHighlight?.colorSlug;
            if (!colorSlug) return false;
            return this.sharedColors.some(color => color.slug === colorSlug);
        },

        /**
         * Whether the submenus (color row, AI, citations, share, more-options) should open downward.
         * They open downward only while the toolbar sits in the upper half of the viewport; otherwise
         * they open upward so they do not extend past the bottom edge. Derived reactively from the
         * toolbar position — a plain watcher only recomputed on the opened false->true transition, so
         * a second selection lower on the page kept a stale direction.
         * @returns {boolean}
         */
        submenuDisplayInDown() {
            const topPosition = parseFloat(this.ui.selectionTool.position.top);
            const viewportHeight = this.device?.height || window.innerHeight;
            return viewportHeight / 2 > topPosition;
        },
    },
    watch: {
        'ui.selectionTool.opened'(opened) {
            this.focusTrapEnabled = opened;
            if (!opened) {
                this.menuIsShowing = false;
                this.menuCopyIsShowing = false;
                this.menuAiIsShowing = false;
                this.menuShareIsShowing = false;
                this.showColorsItems = false;
                this.showWeChatShare = false;
                this.menuMoreOptions = false;
            }
        },
        onlyMobileVisible(isVisible) {
            if (!isVisible && this.showWeChatShare) {
                this.showWeChatShare = false;
            }
        },
    },
    mounted() {
        this.$bus.$on('confirmDeleteSelectionToolHighlight', this.handleConfirmDelete);
        this.$bus.$on('selectionDestroyed', this.releaseDestroyedHighlight);
    },
    beforeDestroy() {
        this.$bus.$off('confirmDeleteSelectionToolHighlight', this.handleConfirmDelete);
        this.$bus.$off('selectionDestroyed', this.releaseDestroyedHighlight);
    },
    methods: {
        ...mapMutations([
            'setCurrentHighlight',
            'removeSelection',
            'hideSelectionTool',
            'openToast',
            'closeSelectionTools',
            'openConfirmationModal',
            'openFlashcardModal',
            'closeNoteEditor',
        ]),
        ...mapActions([
            'listeningHighlights',
            'openNoteEditor',
            'openTranslateIntegration',
            'openDictionaryIntegration',
            'openAiIntegration',
            'createAddOn',
        ]),
        applyColorAndConfirmSelection(color, colorSlug) {
            this.$bus.$emit('applyColorAndConfirmSelection', {
                color,
                colorSlug,
                page: this.ui.selectionTool.page,
            });
        },
        clearSelections() {
            if (window.getSelection) {
                window.getSelection().removeAllRanges();
            } else if (document.selection) {
                document.selection.empty();
            }
        },
        deleteSelection() {
            this.openConfirmationModal({
                title: this.localizations[this.lang].confirmDelete.title,
                message: this.localizations[this.lang].confirmDelete.message,
                confirmLabel: this.localizations[this.lang].delete,
                cancelLabel: this.localizations[this.lang].cancel,
                onConfirmAction: 'confirmDeleteSelectionToolHighlight',
                context: {
                    uuid: this.currentHighlight.uuid,
                    cssClass: this.currentHighlight.getUniqueCssClass(),
                    highlight: this.currentHighlight,
                },
            });
        },
        /**
         * Deletes the confirmed highlight. The host owns it, so the list only changes once
         * the host confirms: a refused delete toasts and leaves the highlight in place (it
         * would come back on the next load anyway), mirroring deleteBookmark. The tool is
         * reset by releaseDestroyedHighlight, on the selectionDestroyed this emits.
         */
        async handleConfirmDelete(context) {
            // A second confirmation while the host has not answered, from this tool or from
            // the notebook, would send a duplicate delete, and its refusal would toast a
            // failure for a highlight already gone.
            if (pendingHighlightDeletes.has(context.uuid)) {
                return;
            }

            pendingHighlightDeletes.add(context.uuid);
            try {
                if (!this.anonymousSession && context.highlight) {
                    const deleted = await context.highlight.destroy();
                    if (!deleted) {
                        this.openToast({ text: this.localizations[this.lang].highlightDeletionFailed });
                        return;
                    }
                }
                this.$store.commit('removeSelection', {
                    uuid: context.uuid,
                });
                this.$bus.$emit('selectionDestroyed', context.cssClass);
            } finally {
                pendingHighlightDeletes.release(context.uuid);
            }
        },
        /**
         * Drops the current highlight once it is gone, whichever surface deleted it, and
         * closes the tool and the note editor anchored to it: the notebook can delete the
         * highlight the tool points at while either is open. The tool owns this cleanup
         * alone, since a second listener reading currentHighlight would race with the
         * reset. Only a tool still pointing at the deleted highlight is reset, since the
         * user may have selected another one while the host was answering.
         */
        releaseDestroyedHighlight(destroyedCssClass) {
            if (destroyedCssClass !== this.currentHighlight?.getUniqueCssClass?.()) {
                return;
            }
            this.setCurrentHighlight({});
            this.hideSelectionTool();
            this.closeNoteEditor();
        },
        showMenu() {
            this.menuIsShowing = !this.menuIsShowing;
        },
        toggleMoreOptions() {
            this.menuAiIsShowing = false;
            this.showWeChatShare = false;
            this.showColorsItems = false;
            this.menuCopyIsShowing = false;
            this.menuMoreOptions = !this.menuMoreOptions;
        },
        toggleCopyMenu() {
            this.menuAiIsShowing = false;
            this.menuShareIsShowing = false;
            this.showWeChatShare = false;
            this.showColorsItems = false;
            this.menuMoreOptions = false;
            this.menuCopyIsShowing = !this.menuCopyIsShowing;
        },
        toggleColorsMenu() {
            this.menuAiIsShowing = false;
            this.menuShareIsShowing = false;
            this.showWeChatShare = false;
            this.menuCopyIsShowing = false;
            this.menuMoreOptions = false;
            this.showColorsItems = !this.showColorsItems;
        },
        toggleAIMenu() {
            this.menuCopyIsShowing = false;
            this.menuShareIsShowing = false;
            this.showWeChatShare = false;
            this.showColorsItems = false;
            this.menuMoreOptions = false;
            this.menuAiIsShowing = !this.menuAiIsShowing;
        },
        toggleShareMenu() {
            this.menuCopyIsShowing = false;
            this.showColorsItems = false;
            this.menuAiIsShowing = false;
            if (this.canUseNativeShare) {
                /**
                 * Always prefer to use the OS native share UI.
                 * In mobile apps this is a must.
                 */
                uiAPI.shareContent({
                    title: this.title,
                    text: this.truncateLongText(this.issue.descriptionRawText || '', 90),
                    url: this.issueHighlightUrlForShare,
                });
            } else {
                /**
                 * But fallback to our custom UI if native share is not present.
                 * For example, this happens in desktop versions of all browsers.
                 */
                this.menuShareIsShowing = !this.menuShareIsShowing;
                this.showWeChatShare = false;
            }
        },
        handleListen(text) {
            this.closeSelectionTools();
            this.listeningHighlights({ text });
        },
        truncateLongText(text, length = 140) {
            if (text.length > length) {
                return `${text.substring(0, length)}...`;
            }

            return text;
        },
        createNote() {
            this.closeSelectionTools();
            this.openNoteEditor();
        },
        createFlashcardFromSelection() {
            this.closeSelectionTools();
            this.openFlashcardModal();
        },
        /**
         * A piece pinned to the passage the reader just selected. A selection
         * the marker hosts could not find again leaves the tool open rather
         * than opening a form over a piece with nowhere to go.
         */
        createAddOnFromSelection() {
            const anchor = textAnchorFromHighlight(this.currentHighlight, {
                isEpub: this.isEpub,
                page: this.currentPage,
            });
            if (!anchor) return;

            this.closeSelectionTools();
            this.createAddOn({ anchor });
        },
        handleTranslate(text) {
            this.openTranslateIntegration({ text });
        },
        handleDictionary(text) {
            this.openDictionaryIntegration({ text });
        },
        showAiIntegration(action) {
            this.maybeCloseOpenedOptions();

            this.openAiIntegration({ text: this.currentHighlight.totalText, action });
        },
        maybeCloseOpenedOptions() {
            this.menuAiIsShowing = false;
            this.menuShareIsShowing = false;
            this.showWeChatShare = false;
            this.menuCopyIsShowing = false;
        },
        onFocusTrapEscape() {
            this.closeSelectionTools();
        },
    },
};
</script>
